"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import React, { useEffect, useMemo, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";

import { dismissAllToasts } from "@/components/design-system/toast/toastDismiss";
import { actions } from "@/store/searchSlice";
import {
  useGetAllShippingAddressesQuery,
  useGetPostalCodeDetailsQuery,
  useGetUserQuery,
  useSetShippingAddressAsPrimaryMutation,
} from "@/store/userApi";

import { useLocationDetails } from "@/app/hooks/useLocationDetails";
import Button from "@/components/design-system/button";
import RadioButton from "@/components/design-system/radioButton";
import TextInputField from "@/components/design-system/textInputField";
import { setCityAndZipcodeCookies } from "@/utils/locationDetails";

import style from "./shippingAddressModal.module.scss";

type Props = {
  showModal: boolean;
  showManualZipCode?: boolean;
  chooseShippingAddressModalCallBack: () => void;
};

export const ChooseShippingAddressModal = ({
  showModal,
  showManualZipCode,
  chooseShippingAddressModalCallBack,
}: Props) => {
  const [zipCode, setZipCode] = useState<string>("");
  const [isValidZipCode, setIsValidZipCode] = useState<boolean>(false);
  const [isZipCodeSubmitted, setIsZipCodeSubmitted] = useState<boolean>(false);
  const [setShippingAddressAsPrimary] = useSetShippingAddressAsPrimaryMutation();
  const [shippingAddresses, setShippingAddresses] = useState<Account.Addresses.Address[]>();
  const [zipCodeDetails, setZipCodeDetails] = useState<ShippingPostalCdDetails | null>(null);
  const dispatch = useDispatch();
  useLocationDetails();
  const pathname = usePathname();

  // pathname is only known on the client; compute the login redirect href here so the
  // Button can render a real <a href> (via the link prop) instead of navigating in onClick.
  const loginHref = useMemo(() => "/login?origin=" + encodeURIComponent(pathname ?? ""), [pathname]);

  const { data: user } = useGetUserQuery();

  const isZipCodeEmpty = zipCode.trim() === "";
  const isLoggedUser = user?.IsGuestUser === false;
  const isAuthorized = user?.isAuthorized === true;

  const addressesData = useGetAllShippingAddressesQuery();
  const postalCodeDetails = useGetPostalCodeDetailsQuery(zipCode);

  useEffect(() => {
    setShippingAddresses(addressesData?.data);
  }, [addressesData?.data, isLoggedUser]);

  const shippingAddressOnChange = (shippingAddressId: string) => {
    const changedShippingAddresses = shippingAddresses?.map((address) => {
      return {
        ...address,
        IsPrimary: address?.Id === parseInt(shippingAddressId),
      };
    });
    setShippingAddresses(changedShippingAddresses);
  };

  const getZipCodeDetails = async () => {
    try {
      setIsZipCodeSubmitted(true);
      if (postalCodeDetails?.data?.isValid) {
        setIsValidZipCode(true);
        setZipCodeDetails(postalCodeDetails?.data);
        setZipCode(postalCodeDetails?.data?.postalCode);
      }
    } catch (error) {
      console.error("Error when getting ZIP code information", error);
    }
  };

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isZipCodeSubmitted) {
      if (isValidZipCode && zipCodeDetails) {
        const city = zipCodeDetails?.city;
        const zipCode = zipCodeDetails?.postalCode;
        setCityAndZipcodeCookies(city, zipCode);
        dispatch(actions.setShippingZipCode(zipCode));
        chooseShippingAddressModalCallBack();
      }
    } else if (!isLoggedUser || (isLoggedUser && !isAuthorized)) {
      console.error("Currently logged out!  Redirecting to login");
      window.location.href = "/login?origin=" + encodeURIComponent(window.location.pathname);
    } else if (isLoggedUser && isAuthorized) {
      const primaryShippingAddress = shippingAddresses?.find((address: any) => address?.IsPrimary === true);
      if (primaryShippingAddress) {
        setCityAndZipcodeCookies(primaryShippingAddress.City, primaryShippingAddress.PostalCD);
        const zipCode =
          primaryShippingAddress?.PostalCD && primaryShippingAddress.PostalCD.length > 5
            ? primaryShippingAddress.PostalCD.slice(0, 5)
            : "";
        if (zipCode) dispatch(actions.setShippingZipCode(zipCode));
        await setShippingAddressAsPrimary(primaryShippingAddress.Id);
        chooseShippingAddressModalCallBack();
      } else {
        console.error("Currently logged out!  Redirecting to login");
        window.location.href = "/login?origin=" + encodeURIComponent(window.location.pathname);
      }
    }
  }

  return (
    <Modal
      onShow={dismissAllToasts}
      className={style.ChooseShippingAddressModal}
      show={showModal}
      onHide={() => {
        chooseShippingAddressModalCallBack();
      }}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title className={style.modalTitle}>Choose your delivery address</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <section className={style.header}>
          <div className={style.subtitle}>
            Select your delivery location for more accurate shipping times and price estimates when shopping on Net32.
          </div>
        </section>

        <Form className={style.form} onSubmit={onSubmit}>
          {isLoggedUser ? (
            <>
              <div>
                {shippingAddresses?.map((shippingAddress, index) => (
                  <div key={shippingAddress.Id} className={style.addressOption}>
                    <div className={style.addressName}>
                      <RadioButton
                        id={`shipping-address-${index}`}
                        name="shipping-address"
                        checked={shippingAddress.IsPrimary}
                        onChange={() => shippingAddressOnChange(shippingAddress.Id.toString())}
                        label={shippingAddress.Name}
                      />
                      {shippingAddress.IsPrimary && <span className={style.primaryBadge}>Primary Address</span>}
                    </div>
                    <div className={style.addressText}>
                      {shippingAddress.Streets.map((street, index2) => (
                        <span className={style.addressStreet} key={street + index2}>
                          {street}
                        </span>
                      ))}
                      , {shippingAddress.City}, {shippingAddress.RegionCD} {shippingAddress.PostalCD}
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <Link prefetch={false} className={style.manageLink} href="/account/shipping">
                  Manage Address Book
                </Link>
              </div>
            </>
          ) : (
            <div className={style.signIn}>
              <Button
                variant="primary"
                color="blue"
                text="Sign in to see your addresses"
                showLeftIcon={false}
                showRightIcon={false}
                link={{ href: loginHref }}
              />
            </div>
          )}
          {showManualZipCode && (
            <div className={style.zipcode}>
              <div className={style.zipcodeTitle}>
                <div className={style.zipcodeLine} />
                <span>or enter a US ZIP code</span>
                <div className={style.zipcodeLine} />
              </div>
              {isZipCodeSubmitted && isValidZipCode ? (
                <div className={style.zipcodeSelected}>
                  <span className={style.zipcodeInfo}>
                    Deliver to <b>{zipCode}</b>
                  </span>
                  <Button
                    variant="tertiary"
                    color="blue"
                    text="Change"
                    showLeftIcon={false}
                    showRightIcon={false}
                    onClick={() => setIsZipCodeSubmitted(false)}
                  />
                </div>
              ) : (
                <div className={style.zipcodeInput}>
                  <div className={style.zipcodeInputGroup}>
                    <div className={style.zipcodeInputWrapper}>
                      <TextInputField
                        label="ZIP Code"
                        value={zipCode}
                        onChange={(e) => setZipCode(e.target.value)}
                        error={isZipCodeSubmitted && !isValidZipCode}
                        errorMessage="ZIP code not found. Please try another ZIP code."
                        size="medium"
                        inputMode="numeric"
                        showLeftIcon={false}
                        showRightIcon={false}
                      />
                    </div>
                    <Button
                      variant="secondary"
                      color="blue"
                      text="Apply"
                      showLeftIcon={false}
                      showRightIcon={false}
                      disabled={isZipCodeEmpty}
                      onClick={() => getZipCodeDetails()}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          <div className={style.formAction}>
            <Button
              variant="primary"
              color="blue"
              text="Done"
              type="submit"
              showLeftIcon={false}
              showRightIcon={false}
            />
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export type ShippingPostalCdDetails = {
  postalCode: string;
  city: string;
  shippingSubRegionId: number;
  isValid: boolean;
  state: string;
  subRegion: string;
};
